// ─────────────────────────────────────────────────────────────────────────────
// SolutionsPage, the "is this for me" page of the public site.
//
// It is a directory, so it is built in two halves and every entry carries an
// anchor the footer links to.
//
//   Half one, by the shape of the business: #retail, #grocery, #production,
//   #wholesale, #omnichannel. Each one describes the operator's day first, then
//   the parts of Wavelength that matter to it, beside a captioned media slot.
//
//   Half two, by the register the store already runs: #toast, #clover, #square,
//   #lightspeed, #shopify, #other. This half is the search core of the page. It
//   is written honestly: the register is good at ringing sales and is staying,
//   and Wavelength runs alongside it. Nothing here claims a direct connector to
//   any point of sale, because none exists; sources are added on request.
//
// The comparison table between the two halves frames the whole argument as what
// a register does against what a back office does, rather than naming a
// competitor and asserting things about it.
//
// Monochrome throughout, headings end in a period, and every image is a grey
// <MediaSlot> captioned with the asset that belongs there. No customer is named
// anywhere on this page.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_PRIMARY,
    BTN_SECONDARY,
    Icon,
    Container,
    Section,
    CardTitle,
    Body,
    Key,
    Eyebrow,
    Pill,
    Badge,
    MediaSlot,
    SectionHead,
    FeatureRow,
    SpecList,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── Half one: the five shapes a business takes ───────────────────────────────
const SHAPES = [
    { id: 'retail', name: 'Multi-location retail' },
    { id: 'grocery', name: 'Grocery and specialty food' },
    { id: 'production', name: 'Butcher and production counters' },
    { id: 'wholesale', name: 'Wholesale and B2B' },
    { id: 'omnichannel', name: 'Online and in store' },
];

// ── Half two: the register in front of the business ──────────────────────────
// `good` is what that system genuinely does well. `gaps` is what it leaves for
// somebody to work out at night. `adds` is what Wavelength puts on top. Nothing
// in here asserts an integration.
const REGISTERS = [
    {
        id: 'toast',
        heading: 'Back office for Toast',
        alongside: 'Toast',
        kind: 'Restaurant and counter service',
        good: 'It rings food fast, handles modifiers, courses and tips, and keeps a line moving on a Friday night. That is a real job and it is doing it. Nothing here asks you to give it up.',
        gaps: [
            'Whether the money from Tuesday arrived, and which line of the bank statement it arrived on',
            'A drawer count the manager cannot see the answer to before they start counting',
            'A processor settlement batch matched back to the orders that produced it',
        ],
        adds: 'A daily close per location with a blind count and a sign-off, then register takings, processor batches and bank deposits matched against each other overnight.',
    },
    {
        id: 'clover',
        heading: 'Back office for Clover',
        alongside: 'Clover',
        kind: 'Countertop and handheld terminals',
        good: 'Clover is quick to buy and quick to stand up, which is why operators often end up with several of them: one per lane, one per store, sometimes one per business the family runs.',
        gaps: [
            'One day across several merchant accounts, instead of one report per terminal',
            'The cash that never touched a terminal: the drop, the safe count and the deposit',
            'A permanent record of who changed a price, a refund or a total, and why',
        ],
        adds: 'Every terminal and every store folded into a single day, cash held to the same standard as card, and every manual change written to an audit log nobody can edit.',
    },
    {
        id: 'square',
        heading: 'Back office for Square',
        alongside: 'Square',
        kind: 'Card payments and light retail',
        good: 'Square takes payments cleanly and gets a new counter trading in an afternoon. Its reporting answers what was sold. The money question starts after that.',
        gaps: [
            'Payouts that arrive netted, several days of sales at a time, against a bank line that names none of them',
            'Reporting across locations that treats a location as a place with a manager, not as a filter',
            'Refunds that need a written reason and an approval before they leave',
        ],
        adds: 'Deposits aged until they clear, payouts traced back to the batch and the store, and refunds gated behind a reason and an approval threshold.',
    },
    {
        id: 'lightspeed',
        heading: 'Back office for Lightspeed',
        alongside: 'Lightspeed',
        kind: 'Retail catalogue and inventory',
        good: 'Lightspeed knows your catalogue. It is comfortable with variants, suppliers and purchase orders, and staff who learned it are fast on it.',
        gaps: [
            'Cash accountability by person and by shift, rather than one figure for the store',
            'A stock number derived from what moved, instead of one typed over during a count',
            'Weighed and produced items carried from the scale through to the report and the export',
        ],
        adds: 'An append-only movement ledger behind the catalogue, a production record from every scale label, and the close and the reconciliation sitting on top of both.',
    },
    {
        id: 'shopify',
        heading: 'Back office for Shopify',
        alongside: 'Shopify',
        kind: 'Online store, with or without a counter',
        good: 'Shopify sells well online and, with its own hardware, at the counter too. Two channels, two payout schedules, and one business that has to agree with itself at the end of the month.',
        gaps: [
            'Online orders and counter sales in one day, under one definition of revenue',
            'Marketing spend matched to the orders and the money it actually produced',
            'Shipping and local delivery followed to the doorstep rather than to the label',
        ],
        adds: 'Both channels in the same tables down to the line item, campaign traffic matched to real orders, and the delivery run visible on the map while it is out.',
    },
    {
        id: 'other',
        heading: 'Back office for anything else',
        alongside: 'whatever each store already runs',
        kind: 'Legacy tills, bespoke systems, a different one per store',
        good: 'Plenty of operators run a till that predates the smartphone, or a different system in every location because each was bought in a different year by a different person.',
        gaps: [
            'A single day across systems that have never agreed on what a day is',
            'A count, a deposit and a sign-off that look the same in every store',
            'Reporting that does not depend on which register a store happens to own',
        ],
        adds: 'The close, the cash controls and the reconciliation run identically on top of whatever each store rings on. New sources are added one at a time, on request.',
    },
];

// ── What a register does against what a back office does ─────────────────────
const COMPARE = [
    {
        pos: 'Rings the sale, takes the tender, prints the receipt',
        back: 'Proves the tender reached the bank, on a named line of a statement',
    },
    {
        pos: 'Reports what was sold, by day, by item, by employee',
        back: 'Matches what was sold against what the processor paid out and what the bank received',
    },
    {
        pos: 'Shows the drawer total at the end of the shift',
        back: 'Hides the expected figure until the money is counted, then records who counted it',
    },
    {
        pos: 'Knows the merchant account it is attached to',
        back: 'Rolls several merchant accounts and several stores into one day',
    },
    {
        pos: 'Lets a manager correct a mistake',
        back: 'Keeps the correction on a permanent log, with a written reason and an approval',
    },
    {
        pos: 'Ends at the store',
        back: 'Ends at the bank statement, the exceptions queue and the journal entry',
    },
];

// ── Where the product stops, said plainly ────────────────────────────────────
const BOUNDARIES = [
    { label: 'Your point of sale', value: 'Stays exactly where it is' },
    { label: 'Bank data', value: 'Statement import, not a live feed' },
    { label: 'Card processing', value: 'Datacap batches read directly, others on request' },
    { label: 'Accounting', value: 'Journal entries export to QuickBooks' },
    { label: 'Margin and cost of goods', value: 'Not tracked' },
    { label: 'Your data', value: 'Exports to CSV, Excel or PDF from every table' },
    { label: 'A new source', value: 'Added on request, one store at a time' },
];

// ── The quiet chip row that closes each vertical ─────────────────────────────
function ModuleRow({ items }) {
    return (
        <div className="mt-10 flex flex-col gap-4 border-t border-[rgba(10,10,10,0.10)] pt-6 sm:flex-row sm:items-center sm:gap-6">
            <span className="flex-none text-[13px] font-medium text-[#A1A1A1]">In the workspace</span>
            <div className="flex flex-wrap gap-2">
                {items.map((m) => <Pill key={m}>{m}</Pill>)}
            </div>
        </div>
    );
}

// ── One register block, anchored so the footer can link straight to it ───────
function RegisterCard({ item }) {
    return (
        <div
            id={item.id}
            className="scroll-mt-24 overflow-hidden rounded-[28px] border border-[rgba(10,10,10,0.10)] bg-white transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-[rgba(10,10,10,0.30)]"
        >
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
                <div className="flex flex-col gap-3 border-b border-[rgba(10,10,10,0.10)] p-6 sm:p-8 lg:border-b-0 lg:border-r">
                    <Eyebrow>{item.kind}</Eyebrow>
                    <CardTitle>{item.heading}</CardTitle>
                    <Body>{item.good}</Body>
                </div>
                <div className="flex flex-col">
                    <div className="px-6 pb-4 pt-6 sm:px-8 sm:pt-8">
                        <span className="text-[13px] font-medium text-[#0A0A0A]">What it does not answer</span>
                    </div>
                    {item.gaps.map((g) => (
                        <div key={g} className="border-t border-[rgba(10,10,10,0.08)] px-6 py-4 sm:px-8">
                            <Body>{g}</Body>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex flex-col gap-2 border-t border-[rgba(10,10,10,0.10)] bg-[#FAFAFA] px-6 py-6 sm:px-8">
                <span className="text-[13px] font-medium text-[#0A0A0A]">With Wavelength on top</span>
                <Body>{item.adds}</Body>
                <span className="mt-1 text-[12.5px] leading-[1.5] text-[#A1A1A1]">
                    Runs alongside {item.alongside}. There is no direct connector; sources are added on request.
                </span>
            </div>
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function SolutionsPage() {
    return (
        <MarketingShell active="solutions">
            <Seo
                title="Back office for the point of sale you already run"
                description="Back office software that works with the point of sale you already run. POS agnostic reporting, cash control and bank reconciliation for multi location retail."
                path="/solutions"
                keywords="back office software that works with my existing POS, POS agnostic reporting for multiple stores, multi location retail back office, back office for Toast, back office for Clover, back office for Square, back office for Lightspeed, back office for Shopify, grocery and specialty food back office, butcher and production tracking software, wholesale invoicing and AR aging, online and in store reconciliation"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': 'https://wavelengtherp.com/solutions#webpage',
                            url: 'https://wavelengtherp.com/solutions',
                            name: 'Back office for the point of sale you already run',
                            description: 'Wavelength by business shape and by register: multi-location retail, grocery and specialty food, butcher and production, wholesale, online and in store, and the back office that runs alongside whichever point of sale each store uses.',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            '@id': 'https://wavelengtherp.com/solutions#breadcrumb',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'Who it is for', item: 'https://wavelengtherp.com/solutions' },
                            ],
                        },
                        {
                            '@type': 'ItemList',
                            '@id': 'https://wavelengtherp.com/solutions#directory',
                            name: 'Wavelength by business shape and by point of sale',
                            itemListOrder: 'https://schema.org/ItemListOrderAscending',
                            numberOfItems: SHAPES.length + REGISTERS.length,
                            itemListElement: [
                                ...SHAPES.map((s, i) => ({
                                    '@type': 'ListItem',
                                    position: i + 1,
                                    name: s.name,
                                    url: `https://wavelengtherp.com/solutions#${s.id}`,
                                })),
                                ...REGISTERS.map((r, i) => ({
                                    '@type': 'ListItem',
                                    position: SHAPES.length + i + 1,
                                    name: r.heading,
                                    url: `https://wavelengtherp.com/solutions#${r.id}`,
                                })),
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero and the index of the page ─────────────────────────── */}
            <PageHero
                eyebrow="Who it is for"
                title="What you run, and what you run it on."
                lead={
                    <>
                        The first half of this page sorts by the shape of the business. The second half sorts by the
                        register standing in front of it. Either way Wavelength is <Key>the back office, not the till</Key>,
                        and it works alongside the point of sale you already have.
                    </>
                }
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            >
                <Reveal>
                    <div className="grid grid-cols-1 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white sm:grid-cols-2">
                        <div className="flex flex-col gap-4 border-b border-[rgba(10,10,10,0.10)] p-6 sm:border-b-0 sm:border-r sm:p-7">
                            <div>
                                <Eyebrow>Part one</Eyebrow>
                                <div className="mt-1 text-[15px] font-medium text-[#0A0A0A]">By the shape of the business</div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {SHAPES.map((s) => (
                                    <Pill key={s.id} as="a" href={`#${s.id}`}>{s.name}</Pill>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-col gap-4 p-6 sm:p-7">
                            <div>
                                <Eyebrow>Part two</Eyebrow>
                                <div className="mt-1 text-[15px] font-medium text-[#0A0A0A]">By the register you already run</div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {REGISTERS.map((r) => (
                                    <Pill key={r.id} as="a" href={`#${r.id}`}>
                                        {r.heading.replace('Back office for ', '')}
                                    </Pill>
                                ))}
                            </div>
                        </div>
                    </div>
                </Reveal>
            </PageHero>

            {/* ── Part one intro ─────────────────────────────────────────── */}
            <Section tone="paper2" pad="md">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="Part one"
                            title="By the shape of the business."
                            lead="Five shapes, and most operators are two or three of them at once. The parts of Wavelength that matter change with the shape; the close, the cash controls and the reconciliation underneath do not."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Multi-location retail ──────────────────────────────────── */}
            <Section id="retail" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Multi-location retail"
                            title="Three stores, three closes, one morning."
                            body="The day ends at a different hour in every store, and by the time the totals reach you they have been through a notebook, a photograph of a deposit slip and somebody's memory of a busy afternoon. Wavelength gives each location its own close and gives you all of them in one list before the first delivery arrives."
                            bullets={[
                                'A separate daily close per location, locked the moment the manager signs it',
                                'Over and short by store, by shift and by person, not buried in a company total',
                                'Owner-only approval on refunds, voids and anything else that moves money',
                                'One exceptions queue for every store, with the location, source, date and amount',
                            ]}
                            cta={<Link to="/how-it-works" className={BTN_SECONDARY}>See how a day closes<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Close status across four locations on a laptop"
                                    note="One row per store, three signed off and one still open, early morning light on the desk"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <ModuleRow items={['Daily close', 'Cash accountability', 'Exceptions queue', 'Audit log', 'Roles per store', 'Workspace switching']} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Grocery and specialty food ─────────────────────────────── */}
            <Section id="grocery" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            reverse
                            eyebrow="Grocery and specialty food"
                            title="Every tender you take, traced to the deposit."
                            body="A food counter takes cash, cards, benefit programs, gift certificates and house accounts inside the same ten minutes, and each one settles on a different clock. Wavelength keeps a ledger per tender, so the day adds up before anyone drives to the bank."
                            bullets={[
                                'Cash, card, EBT and SNAP, gift certificates and online payments, each on their own ledger',
                                'Gift certificate liability carried as a balance rather than estimated at year end',
                                'Weighed items priced by the pound from the scale through to the report and the export',
                                'Peak hours, product ranking and seasonal lift, per store and per channel',
                            ]}
                            cta={<Link to="/features#reconciliation" className={BTN_SECONDARY}>See tender reconciliation<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Tender mix for one trading day"
                                    note="Laptop screenshot, a column per tender with the unmatched rows called out"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <ModuleRow items={['Payment mix', 'Bank statements', 'Gift certificates', 'Coupons', 'Peak hours', 'Inventory']} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Butcher and production counters ────────────────────────── */}
            <Section id="production" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Butcher and production counters"
                            title="From the case at the door to the label on the pack."
                            body="Product arrives on a truck, gets broken down, gets weighed, gets wrapped, and somewhere in that morning the number in the system stops matching the number in the room. Wavelength follows the material instead of trusting a count."
                            bullets={[
                                'Receiving signed off on an iPad at the door, with a temperature check and the ordered against received variance',
                                'Every scale label becomes a production record, rolled up by product, scale and staff member',
                                'Label formats defined in the office and pushed out to the scales',
                                'Yield and waste tracked from raw material to finished pack on an append-only ledger',
                            ]}
                            cta={<Link to="/features#inventory" className={BTN_SECONDARY}>See production and receiving<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Photo"
                                    label="A scale printing a label at the production counter"
                                    note="Close in on the wrapped pack and the label coming off the printer, cold neutral light"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <ModuleRow items={['Receiving', 'Production records', 'Scale label formats', 'Yield and waste', 'Raw materials', 'Movement ledger']} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Wholesale and B2B ──────────────────────────────────────── */}
            <Section id="wholesale" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            reverse
                            eyebrow="Wholesale and B2B"
                            title="Know who owes you, and since when."
                            body="Wholesale is retail with a memory. The order goes out today and the money comes in some other week, and the only way to keep that honest is a ledger per account. Invoices are raised from the orders you already took, and nothing is deleted to make a balance look better."
                            bullets={[
                                'An invoice raised automatically from every wholesale order, numbered by the order it came from',
                                'Ten price levels set as a percentage off retail, with per-customer overrides',
                                'Partial payments and credit memos on the ledger, so an invoice is credited and never erased',
                                'Receivables aged by bucket, with branded invoice PDFs from the same page',
                            ]}
                            cta={<Link to="/features" className={BTN_SECONDARY}>See wholesale billing<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Receivables aged by bucket"
                                    note="Laptop screenshot, one row per account, the oldest balances at the top"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <ModuleRow items={['Wholesale invoicing', 'Price levels', 'Credit memos', 'AR aging', 'Customers', 'Branded exports']} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Online and in store ────────────────────────────────────── */}
            <Section id="omnichannel" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Online and in store"
                            title="One day, whether it was rung or clicked."
                            body="The website and the counter are two businesses until they share a definition of a day. Wavelength puts online orders and counter sales in the same tables with the same statuses and the same tenders, so a question has one answer instead of two that disagree."
                            bullets={[
                                'Website orders and counter sales side by side, down to the line item',
                                'Campaign traffic matched to the orders and the revenue it actually produced',
                                'Shipping and local delivery on the map, with the driver visible while the run is out',
                                'Repeat rate, average order value and lifetime value across both channels',
                            ]}
                            cta={<Link to="/features#sales" className={BTN_SECONDARY}>See reporting<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Order database with website and counter orders interleaved"
                                    note="Laptop screenshot, one table, the channel shown as a column"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <ModuleRow items={['Order database', 'Campaigns', 'Delivery map', 'Customers', 'Packing app', 'Notifications']} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Most operators are more than one shape ─────────────────── */}
            <Section tone="paper2" pad="md">
                <Container>
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] bg-[#111111] p-6 text-white sm:p-12 lg:p-14">
                            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
                                <div className="flex flex-col">
                                    <h2 className="max-w-[460px] text-[28px] font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-[36px]">
                                        Most operators are more than one of these.
                                    </h2>
                                    <p className="mt-5 max-w-[460px] text-[16px] leading-[1.6] text-white/60">
                                        A shop with a production counter, a wholesale round and a website is already four
                                        of the five, and it is usually ringing sales on two different systems while it is
                                        at it. The point of one back office is that none of that has to become four
                                        separate ones.
                                    </p>
                                    <div className="mt-8 flex flex-wrap gap-2">
                                        {SHAPES.map((s, i) => (
                                            <Pill key={s.id} dark active={i === 0}>{s.name}</Pill>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <MediaSlot
                                        dark
                                        kind="Image"
                                        label="One workspace switching between two businesses"
                                        note="Straight-on screenshot of the store switcher open at the top of the screen"
                                        ratio="3 / 4"
                                        className="!rounded-[28px] max-w-[320px]"
                                    />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Part two intro and the comparison table ────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="Part two"
                            title="By the register you already run."
                            lead="Wavelength is back office software that works with the point of sale you already have. It is deliberately register agnostic: reporting across multiple stores, cash control and reconciliation run the same way whether every location rings on the same system or none of them do."
                            actions={<Link to="/integrations" className={BTN_SECONDARY}>How sources connect<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>

                    <Reveal delay={80}>
                        <div className="mt-12 overflow-hidden rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-white">
                            <div className="hidden grid-cols-2 sm:grid">
                                <div className="border-r border-[rgba(10,10,10,0.10)] px-6 py-4">
                                    <span className="text-[13px] font-medium text-[#A1A1A1]">What a register does</span>
                                </div>
                                <div className="px-6 py-4">
                                    <span className="text-[13px] font-medium text-[#A1A1A1]">What a back office does</span>
                                </div>
                            </div>
                            {COMPARE.map((row) => (
                                <div key={row.pos} className="grid grid-cols-1 border-t border-[rgba(10,10,10,0.10)] sm:grid-cols-2">
                                    <div className="px-6 py-5 sm:border-r sm:border-[rgba(10,10,10,0.10)]">
                                        <span className="mb-1.5 block text-[12px] text-[#A1A1A1] sm:hidden">Register</span>
                                        <span className="text-[14px] leading-[1.55] text-[#6B6B6B]">{row.pos}</span>
                                    </div>
                                    <div className="border-t border-[rgba(10,10,10,0.06)] px-6 py-5 sm:border-t-0">
                                        <span className="mb-1.5 block text-[12px] text-[#A1A1A1] sm:hidden">Back office</span>
                                        <span className="text-[14px] font-medium leading-[1.55] text-[#0A0A0A]">{row.back}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={120}>
                        <div className="mt-4">
                            <MediaSlot
                                kind="Screenshot"
                                label="The same close screen with three different registers named as the source"
                                note="Wide laptop screenshot, one day, three stores, the source column doing the work"
                                ratio="16 / 9"
                                className="!rounded-[28px]"
                            />
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── The registers themselves ───────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Find yours."
                            lead="Each of these is good at ringing sales, and each of them is staying. What follows is only the part of the evening it leaves to you."
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <div className="mt-12 flex flex-col gap-4">
                        {REGISTERS.map((r, i) => (
                            <Reveal key={r.id} delay={i === 0 ? 0 : 80}>
                                <RegisterCard item={r} />
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={120}>
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                            {['Keep your register', 'No connector required', 'Sources added on request', 'One dashboard for every store'].map((t) => (
                                <Badge key={t}>{t}</Badge>
                            ))}
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Where it stops ─────────────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <SectionHead
                                    title="Where it stops."
                                    lead="Software that claims everything is software you have to check. This is the edge of what Wavelength does, so you can tell in a minute whether it fits."
                                    maxWidth="max-w-[340px]"
                                />
                                <div className="mt-2">
                                    <Link to="/contact" className={BTN_PRIMARY}>Ask about your setup<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <div className="flex flex-col gap-4">
                                <SpecList rows={BOUNDARIES} />
                                <Body className="px-1">
                                    Wavelength is not your accounting system and is not trying to become one. It closes
                                    the day, proves the cash, reconciles the money and hands your bookkeeper a clean
                                    journal entry.
                                </Body>
                            </div>
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Get started"
                title="Keep your register. We will reconcile it."
                body="Tell us what each store rings on and we will show you the same day closed, counted and matched to the bank."
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            />
        </MarketingShell>
    );
}
